import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { LocalStorageService, SessionStorageService } from '@services/storage.service';
import { ServiceItem } from '@shared/serviceitem';
import { serviceItems } from '../topbar/servicemenu';
import { AuthService } from './auth.service';

@Injectable({
	providedIn: 'root',
})
export class AuthGuardService {
	services: ServiceItem[] = serviceItems;
	permissions: string[] = [];

	constructor(
		private router: Router,
		private authService: AuthService,
		private sessionStorageService: SessionStorageService,
		private localStorageService: LocalStorageService,
	) {
		this.setUserPermissionsInSessionStorage();
	}

	setUserPermissionsInSessionStorage(): void {
		try {
			this.permissions = JSON.parse(this.sessionStorageService.get('User.Permissions')) as string[];
		} catch (error: unknown) {
			console.log('sessionStorageService.get("User.Permissions") Error');
			this.permissions = [];
		}
	}

	canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
		this.setUserPermissionsInSessionStorage();
		const isAuth = this.authService.isLoggedIn();
		const selectedAccount = this.sessionStorageService.get('User.Account');
		const locationState = state.url.startsWith('/') ? state.url.substring(1) : state.url;
		const index = this.services.findIndex((x) => locationState.includes(x.link.split('/')[0]));
		if (!isAuth) {
			const tempUser = this.sessionStorageService.get('User.Email');
			const tempRemember = this.sessionStorageService.get('User.RememberMe');
			const tempRefresh = this.sessionStorageService.get('User.RefreshToken');
			const tempClientId = this.sessionStorageService.get('User.ClientId');
			const tempAdLogin = this.sessionStorageService.get('AdLogin');
			const tempIAMId = this.sessionStorageService.get('User.IAM.Id');
			const tempLanguage = this.sessionStorageService.get('User.PreferredLanguage');
			const tempNavigationDeepLink = this.sessionStorageService.get('Navigation.DeepLink');
			this.sessionStorageService.clear();

			if (tempRefresh) this.sessionStorageService.set('User.RefreshToken', tempRefresh);
			if (tempClientId) this.sessionStorageService.set('User.ClientId', tempClientId);
			if (tempAdLogin) this.sessionStorageService.set('AdLogin', tempAdLogin);
			if (tempIAMId) this.sessionStorageService.set('User.IAM.Id', tempIAMId);
			if (tempUser) {
				this.sessionStorageService.set('User.Email', tempUser);
				this.localStorageService.set('User.Email', tempUser);
			}
			if (tempLanguage) {
				this.sessionStorageService.set('User.PreferredLanguage', tempLanguage);
			}

			if (tempNavigationDeepLink) {
				this.sessionStorageService.set('Navigation.DeepLink', tempNavigationDeepLink);
			}

			if (tempRemember) {
				this.sessionStorageService.set('User.RememberMe', tempRemember);
				this.localStorageService.set('User.RememberMe', tempRemember);
			}

			const urlTree = this.router.parseUrl(state.url);
			if (urlTree.queryParams['accountId']) {
				this.sessionStorageService.set('User.DeepLink.Account', urlTree.queryParams['accountId']);
			}

			let hasInviteToken = false;
			if (index >= 0) {
				if (!this.services[index].enabled) {
					sessionStorage.setItem('Link.Illegal', 'x');
				}
			} else {
				if (urlTree.queryParams['token']) {
					hasInviteToken = true;
					this.sessionStorageService.set('User.InviteToken', urlTree.queryParams['token'].toString());
				}
			}
			void this.router.navigate(['signin'], hasInviteToken ? { queryParams: { invited: 1 } } : {});
		} else {
			if (index >= 0) {
				const hasSuperadminPermission = Array.isArray(this.permissions) ? this.permissions.includes('iam:OrganizationsPut') : false;
				// this is a special override for SUPER_ADMINS that checks if the
				// route is "setup" and if the user has SUPER_ADMIN permissions
				if (this.services[index].link.startsWith('setup') && hasSuperadminPermission) {
					return true;
				}
				if (this.services[index].label === 'Sales' && (this.localStorageService.get('User.Email').toLowerCase().includes('@acies.dk') || this.localStorageService.get('User.Email').toLowerCase().includes('@elitfonster.se'))) {
					return true;
				}
				// A One Monitor deep link names its account and One Monitor selects it, so the link opens in a window with no account selected yet.
				const oneMonitorDeepLink = locationState.startsWith('administration/sync-monitoring') && !!this.router.parseUrl(state.url).queryParams['accountId'];
				if (this.services[index].enabled && (selectedAccount || oneMonitorDeepLink)) {
					const permissions = this.services[index].permission.split('|');
					return (
						this.services[index].permission === '' ||
						permissions.some((permission) => {
							return permissions?.includes(permission);
						})
					);
				} else {
					sessionStorage.setItem('Link.Illegal', 'x');
					void this.router.navigate(['core']);
					return false;
				}
			}
		}
		return isAuth;
	}
}

export const AuthGuard: CanActivateFn = (next: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean => {
	return inject(AuthGuardService).canActivate(next, state);
};
